import xssModule, { type IWhiteList } from 'xss';

// `xss` is CommonJS and assigns most helpers in a loop, so Node can't expose them as named ESM
// exports; they all live on the default export, which the package typings model as `filterXSS` only.
const xss = xssModule as unknown as typeof import('xss');

/**
 * Hosts (and their subdomains) that CMS and product HTML may load images and media from.
 * Content is otherwise limited to same-origin URLs and inline `data:image/` URIs.
 */
const TRUSTED_MEDIA_HOSTS = [
    'eataly.it',
    'eataly.net',
    'eataly.de',
    'eataly.com',
    'eataly.fr',
    'demandware.net',
    'commercecloud.salesforce.com',
    'cc.salesforce.com',
    // Non-production instance hosts; SCAPI resolves library `?$staticlink$` images to the instance host.
    'eataly.nco.farm',
];

/** Attributes the browser fetches as soon as the element renders, without a user click. */
const MEDIA_URL_ATTRIBUTES = new Set(['src', 'srcset', 'poster']);

const GLOBAL_ATTRIBUTES = ['class', 'id', 'style', 'title', 'dir', 'lang', 'role'];

/** Tags removed together with their content instead of being unwrapped. */
const STRIPPED_TAG_BODIES = [
    'script',
    'style',
    'noscript',
    'template',
    'textarea',
    'title',
    'iframe',
    'object',
    'svg',
    'math',
    'select',
];

/** CSS functions that load a resource from a bare string argument, so the URL can't be checked like `url()`. */
const CSS_RESOURCE_FUNCTION_RE = /(?:^|[^\w-])(?:-webkit-)?(?:image-set|image|src|cross-fade|element)\s*\(/i;
const CSS_URL_RE = /url\(\s*(['"]?)(.*?)\1\s*\)/gi;

const RELATIVE_URL_BASE = 'https://relative.invalid/';

function isTrustedMediaUrl(rawUrl: string): boolean {
    const value = rawUrl.trim();
    if (value.toLowerCase().startsWith('data:image/')) {
        return true;
    }
    let url: URL;
    try {
        // Resolving against a sentinel base parses URLs the way browsers do, so tricks like
        // protocol-relative `//host` or `/\host` resolve to their real host instead of looking relative.
        url = new URL(value, RELATIVE_URL_BASE);
    } catch {
        return false;
    }
    if (url.href.startsWith(RELATIVE_URL_BASE)) {
        return true;
    }
    return (
        (url.protocol === 'https:' || url.protocol === 'http:') &&
        TRUSTED_MEDIA_HOSTS.some((host) => url.hostname === host || url.hostname.endsWith(`.${host}`))
    );
}

function isTrustedSrcset(srcset: string): boolean {
    return srcset.split(',').every((candidate) => isTrustedMediaUrl(candidate.trim().split(/\s+/)[0] ?? ''));
}

function isSafeCssValue(value: string): boolean {
    // CSS escapes (`u\72l(`) could hide a function name from the checks below.
    if (value.includes('\\') || CSS_RESOURCE_FUNCTION_RE.test(value)) {
        return false;
    }
    return Array.from(value.matchAll(CSS_URL_RE)).every(([, , url]) => isTrustedMediaUrl(url ?? ''));
}

function buildAllowList(): IWhiteList {
    const allowList: IWhiteList = {
        ...xss.getDefaultWhiteList(),
        picture: [],
        source: ['src', 'srcset', 'sizes', 'media', 'type', 'width', 'height'],
        time: ['datetime'],
        wbr: [],
    };
    allowList.a = [...(allowList.a ?? []), 'rel'];
    allowList.img = [...(allowList.img ?? []), 'srcset', 'sizes', 'decoding'];
    allowList.ol = [...(allowList.ol ?? []), 'start', 'reversed', 'type'];
    allowList.li = [...(allowList.li ?? []), 'value'];
    for (const tag of Object.keys(allowList)) {
        allowList[tag] = [...(allowList[tag] ?? []), ...GLOBAL_ATTRIBUTES];
    }
    return allowList;
}

const filter = new xss.FilterXSS({
    whiteList: buildAllowList(),
    stripIgnoreTag: true,
    stripIgnoreTagBody: STRIPPED_TAG_BODIES,
    // Keep every CSS property except declarations that load a resource from an untrusted host.
    css: {
        whiteList: {},
        onIgnoreAttr: (name: string, value: string) => `${name}:${value}`,
        safeAttrValue: (_name: string, value: string) => (isSafeCssValue(value) ? value : ''),
    },
    onIgnoreTagAttr: (_tag, name, value) => {
        if (name.startsWith('data-') || name.startsWith('aria-')) {
            return `${name}="${xss.escapeAttrValue(xss.friendlyAttrValue(value))}"`;
        }
    },
    safeAttrValue: (tag, name, value, cssFilter) => {
        if (MEDIA_URL_ATTRIBUTES.has(name)) {
            const url = xss.friendlyAttrValue(value);
            if (!(name === 'srcset' ? isTrustedSrcset(url) : isTrustedMediaUrl(url))) {
                return '';
            }
        }
        return xss.safeAttrValue(tag, name, value, cssFilter);
    },
});

/**
 * Sanitizes CMS- or product-authored HTML before it is rendered with `dangerouslySetInnerHTML`.
 *
 * Keeps common rich-text markup, classes and inline styles, but removes scripts, event handlers,
 * embeds and forms, and drops images, media and CSS backgrounds that point outside
 * {@link TRUSTED_MEDIA_HOSTS}. Unclosed tags are escaped, which defuses dangling-markup injection.
 * This matters because the CSP allows any HTTPS image source, so the CSP alone no longer stops
 * injected content from sending requests to arbitrary hosts.
 */
export function sanitizeHtml(html: string): string {
    return filter.process(html);
}
